import { Injectable } from '@angular/core';
import { Field, FieldTwo, MultiSelectFieldTwo, Option, OptionTwo, SelectFieldTwo } from 'src/app/demo/interfaces/form-print.interface';
import { AuthService } from '../service/auth.service';


@Injectable({
   providedIn: 'root'
})
export class CoreUtilsService {

   constructor(
      private authService: AuthService,
   ) { }

   /**
     * @author Ministerio de Cultura
     * @createdate 2025-04-28
     * @description
     * ofrece buscar o seleccionar variables locales en el ts, esto permite realizar
     * consultas dinamicas de la variables
     * @param path
     * @returns
     *
     */
   getTargetFromPath(base: any, path: string): { parent: any, key: string | number | null } {
      const regex = /\[['"]?([^'"\[\]]+)['"]?\]/g;
      const keys = Array.from(path.matchAll(regex), m => m[1]);

      if (keys.length === 0) return { parent: null, key: null };

      const lastKey = keys.pop()!;
      const parent = keys.reduce((acc, key) => acc?.[isNaN(Number(key)) ? key : Number(key)], base);
      const parsedLastKey = isNaN(Number(lastKey)) ? lastKey : Number(lastKey);

      return { parent, key: parsedLastKey };
   }

   /* ---------------------------------------------- Version 1 --------------------------------------------------------- */
   /**
    * @author Ministerio de Cultura
    * @createdate 2025-04-09
    * @description
    * Actualiza el valor (`value`) de un campo específico dentro de una sección de un formulario.
    *
    * Es útil cuando el usuario selecciona o modifica un valor y se desea reflejar ese cambio
    * de forma reactiva en la interfaz.
    *
    * @param formStepKey Clave del paso del formulario que agrupa varias secciones.
    * @param formSection Sección específica del formulario donde se encuentra el campo.
    * @param position Índice del campo dentro de la sección que se va a actualizar.
    * @param value Nuevo valor a asignar al campo.
    */
   updateFieldValue(fields: Field[], position: number, values: { [key: string]: any }): Field[] {
      const updated = [...fields];
      updated[position] = { ...updated[position], ...values };
      return updated;
   }

   /**
     * @author Ministerio de Cultura
     * @createdate 2025-04-09
     * @description
     * Asigna dinámicamente las opciones (`options`) a un campo de tipo select dentro del formulario,
     * a partir de una respuesta de datos remota. Además, habilita el campo para permitir su selección.
     *
     * Comúnmente utilizado en formularios en cascada donde las opciones dependen del valor de otro campo.
     *
     * @param formStepKey Clave del paso del formulario.
     * @param formSection Sección donde se encuentra el campo.
     * @param position Índice del campo al que se le van a asignar opciones.
     * @param data Array de datos desde el cual se construyen las opciones del campo.
     * @param type Define cómo se deben extraer los valores `text` y `value` de cada ítem.
     */
   setFieldOptions(fields: Field[], position: number, data: any[], type: Option): Field[] {
      const updated = [...fields];
      updated[position] = {
         ...updated[position],
         options: data.map(item => ({
            text: item[type.text],
            value: item[type.value]
         }))
      };
      return updated
   }

   /**
      * @author Ministerio de Cultura
      * @createdate 2025-04-09
      * @description
      * Activa o desactiva un campo específico dentro de una sección del formulario.
      *
      * Es útil para controlar la interacción del usuario según condiciones dinámicas (por ejemplo, país seleccionado).
      *
      * @param formStepKey Clave del paso del formulario.
      * @param formSection Sección del formulario que contiene el campo.
      * @param position Índice del campo dentro de la sección.
      * @param _disabled Estado de desactivación. `true` lo desactiva, `false` lo habilita. Por defecto, se desactiva.
      */
   setFieldDisabled(fields: Field[], position: number, _disabled: boolean = false): Field[] {
      const updated = [...fields];
      updated[position] = { ...updated[position], disabled: _disabled };
      return updated;
   }

   /**
     * @description
     * Reinicia (limpia y desactiva) todos los campos desde un índice dado dentro de una sección del formulario.
     *
     * Esta función se utiliza típicamente en formularios en cascada para limpiar campos dependientes
     * cuando el valor de un campo anterior cambia.
     *
     * @param formStepKey Clave del paso del formulario.
     * @param formSection Sección que contiene los campos a reiniciar.
     * @param startIndex Índice a partir del cual se deben limpiar los campos.
     */
   resetCascadeFromIndex(fields: Field[], startIndex: number): Field[] {
      const updated = [...fields];
      for (let i = startIndex; i < updated.length; i++) {
         updated[i] = {
            ...updated[i],
            value: null,
            options: [],
            disabled: true
         };
      }
      return updated;
   }

   /* ---------------------------------------------- Version 2 --------------------------------------------------------- */
   /**
    * @author Ministerio de Cultura
    * @createdate 2026-04-20
    * @description
    * Actualiza el valor (`value`) de un campo específico dentro de una sección de un formulario.
    *
    * Es útil cuando el usuario selecciona o modifica un valor y se desea reflejar ese cambio
    * de forma reactiva en la interfaz.
    *
    * @param fields Arreglo de campos (`FieldTwo[]`) del formulario.
    * @param keyField Clave (`key`) del campo al que se le asignarán las opciones.
    * @param values Nuevo valor a asignar al campo.
    */
   getFieldValueByKey(fields: FieldTwo[], keyField: string): any {
      const position = fields.findIndex(item => item.key === keyField);
      return fields[position].value;
   }

   /**
    * @author Ministerio de Cultura
    * @createdate 2025-05-18
    * @description
    * Actualiza el valor (`value`) de un campo específico dentro de una sección de un formulario.
    *
    * Es útil cuando el usuario selecciona o modifica un valor y se desea reflejar ese cambio
    * de forma reactiva en la interfaz.
    *
    * @param fields Arreglo de campos (`FieldTwo[]`) del formulario.
    * @param keyField Clave (`key`) del campo al que se le asignarán las opciones.
    * @param values Nuevo valor a asignar al campo.
    */
   updateFieldValueByKey(fields: FieldTwo[], keyField: string, values: { [key: string]: any }): FieldTwo[] {
      const position = fields.findIndex(item => item.key === keyField);
      const updated = [...fields];
      updated[position] = { ...updated[position], ...values };
      return updated;
   }

   /**
    * @author Ministerio de Cultura
    * @createdate 2025-06-17
    * @description
    * Habilita o deshabilita un campo específico dentro del formulario.
    *
    * Es útil para controlar la interacción del usuario según condiciones dinámicas
    * (por ejemplo, al seleccionar cierto país o al aplicar validaciones condicionales).
    *
    * @param fields Arreglo de campos (`FieldTwo[]`) del formulario.
    * @param keyField Clave (`key`) del campo que se desea habilitar o deshabilitar.
    * @param _disabled Estado del campo: `true` lo desactiva, `false` lo habilita. Por defecto, `true`.
    * @returns El arreglo de campos actualizado con el cambio de estado aplicado.
    */
   setFieldDisabledByKey(fields: FieldTwo[], keyField: string, _disabled: boolean = false): FieldTwo[] {
      const position = fields.findIndex(item => item.key === keyField);
      const updated = [...fields];
      updated[position] = { ...updated[position], disabled: _disabled };
      return updated;
   }

   setFieldHidenByKey(fields: FieldTwo[], keyField: string, hidden: boolean = false): FieldTwo[] {
      const position = fields.findIndex(item => item.key === keyField);
      const updated = [...fields];
      updated[position] = { ...updated[position], show: !hidden };
      return updated;
   }

   setFieldColumnByKey(fields: FieldTwo[], keyField: string, column: string): FieldTwo[] {
      const position = fields.findIndex(item => item.key === keyField);
      const updated = [...fields];
      updated[position] = { ...updated[position], col: column };
      return updated;
   }

   /**
    * @author Ministerio de Cultura
    * @createdate 2025-07-07
    * @description
    * ⚠️ Resetea tanto las opciones (`options`) como el valor (`value`) de un campo de tipo `select` o `multiselect` dentro del formulario.
    *
    * Este método es útil cuando el campo depende de otro (comportamiento en cascada), ya que limpia su estado actual antes de asignar nueva información proveniente de un servicio (API).
    *
    * ⚠️ Asegúrese de ejecutar este método **antes de** cargar nuevas opciones dinámicas, para evitar valores obsoletos o inconsistencias.
    *
    * @param fields Arreglo de campos (`FieldTwo[]`) del formulario.
    * @param keyField Clave (`key`) del campo al que se desea limpiar las opciones y el valor.
    * @returns Arreglo de campos actualizado, con el campo objetivo reiniciado.
    */
   resetFieldOptionsAndValueByKey(fields: SelectFieldTwo[] | MultiSelectFieldTwo[], keyField: string) {
      const position = fields.findIndex(item => item.key === keyField);
      const updated = [...fields];
      updated[position] = {
         ...updated[position],
         options: [],
         value: undefined,
         tooltip: undefined,
      };
      return updated
   }

   /**
    * @author Ministerio de Cultura
    * @createdate 2025-06-18
    * @description
    * Asigna dinámicamente las opciones (`options`) a un campo de tipo `select` o `multiselect` dentro del formulario,
    * a partir de un arreglo de datos remoto. Cada opción se construye con base en la estructura proporcionada en `type`.
    *
    * Es útil para poblar campos select o multiselect con información obtenida por servicios (API).
    *
    * @param fields Arreglo de campos (`FieldTwo[]`) del formulario.
    * @param keyField Clave (`key`) del campo al que se le asignarán las opciones.
    * @param data Arreglo de datos desde el cual se construirán las opciones del campo.
    * @param type Objeto que define la estructura de cada opción (`text`, `value`, `keyValue` y opcionalmente `tooltip`).
    * @returns Arreglo de campos actualizado con las nuevas opciones asignadas.
    */
   setFieldOptionsByKey(fields: SelectFieldTwo[] | MultiSelectFieldTwo[], keyField: string, data: any[], structureOfOptions: OptionTwo): FieldTwo[] {
      const position = fields.findIndex(item => item.key === keyField);
      const updated = [...fields];
      updated[position] = {
         ...updated[position],
         options: data.map(item => {
            const option: any = {};
            // 🔑 Recorremos todas las claves definidas en structureOfOptions
            for (const key in structureOfOptions) {
               if (structureOfOptions.hasOwnProperty(key)) {
                  // El valor de structureOfOptions[key] es el nombre real en "item"
                  const sourceKey = structureOfOptions[key];
                  option[key] = item[sourceKey as string];
               }
            }

            return option;
         })
      };
      return updated
   }

   /**
    * @author Ministerio de Cultura
    * @createdate 2026-05-19
    * @description
    * Consulta y retorna todas las opciones de un campo de tipo `select` o `multiselect` que coincida con el keyField.
    *
    * @param fields Arreglo de campos {@link FieldTwo[]} del formulario.
    * @param keyField Clave {@link key} del campo al que se desea asignar la selección.
    * @returns El arreglo de opciones del campo (vacío si no existe el campo).
    */
   getFieldOptionsByKey(fields: SelectFieldTwo[] | MultiSelectFieldTwo[], keyField: string): OptionTwo[] {
      const field = fields.find(item => item.key === keyField);
      return field?.options ?? [];
   }

   /**
    * @author Ministerio de Cultura
    * @createdate 2026-05-19
    * @description
    * Consulta y retorna una sola opcion de un campo de tipo `select` o `multiselect` que coincida con el keyField y el value.
    *
    * @param fields Arreglo de campos {@link FieldTwo[]} del formulario.
    * @param keyField Clave {@link key} del campo al que se desea asignar la selección.
    * @param value Valor a buscar dentro de las opciones (string | number).
    * @returns La opción encontrada o `null` si no existe.
    */
   getFieldOptionByKeyAndValue(fields: SelectFieldTwo[] | MultiSelectFieldTwo[], keyField: string, value: string | number): OptionTwo | null {
      const field = fields.find(item => item.key === keyField);
      return field?.options?.find(item => item.value == value) ?? null;
   }

   /**
    * @author Ministerio de Cultura
    * @createdate 2025-07-07
    * @description
    * Asigna como valor seleccionado el ítem de un campo de tipo `select` o `multiselect` que coincida con el `matchKeyValue`.
    * Este método busca dentro de las opciones del campo y selecciona el valor correspondiente
    * al `keyValue` proporcionado.
    *
    * @param fields Arreglo de campos (`FieldTwo[]`) del formulario.
    * @param keyField Clave (`key`) del campo al que se desea asignar la selección.
    * @param matchKeyValue Valor de `keyValue` que se utilizará para buscar la opción a seleccionar.
    * @returns El arreglo de campos actualizado con la selección aplicada.
    */
   selectOptionByKey(fields: SelectFieldTwo[] | MultiSelectFieldTwo[], keyField: string, matchKeyValue: string | number) {
      const position = fields.findIndex(item => item.key === keyField);
      const updated = [...fields];
      const option = updated[position].options.find((option: OptionTwo) => option.keyValue === matchKeyValue);
      updated[position] = { ...updated[position], value: option?.value };
      return updated;
   }

   /**
    * @author Ministerio de Cultura
    * @createdate 2025-05-08
    *
    * Verifica el acceso del usuario autenticado a un conjunto de endpoints específicos,
    * utilizando los permisos definidos en `apis_data` del token decodificado.
    *
    * @param endpoints - Arreglo de rutas (endpoints) a verificar.
    * @returns Un arreglo de objetos que indica si cada endpoint está deshabilitado (`isDisabled: true`) o no (`isDisabled: false`).
    *
    * @example
    * checkAccessToApis(['/endpoint/a', '/endpoint/b']);
    * // Retorna: [{ isDisabled: false }, { isDisabled: true }]
    *
    * Este método normaliza las rutas del token para compararlas con los endpoints esperados.
    * La normalización convierte guiones bajos (`_`) en barras (`/`) para asegurar la coincidencia.
    */
   checkAccessToApis(endpoints: string[]): { isDisabled: boolean }[] {
      const normalizedApis = this.authService
         .decodeTokenUser()
         .apis_data
         .map((api: any) => this.normalizeApiPath(api));
      const accessToApis = endpoints.map(endpoint => ({
         isDisabled: !normalizedApis.includes(endpoint)
      }));
      return accessToApis;
   }

   /**
    * @author Ministerio de cultura
    * @createdate 2025-05-08
    * Metodo quita los parametros dinamicos de la url.
   */
   private normalizeApiPath(apiPath: string): string {
      return apiPath.replace(/_/g, '/');
   }

}
